import React, { useCallback, useEffect, useRef, useState } from 'react';
import NotificationPanel from './NotificationPanel';
import Pagination from './Pagination';
import { API } from '../api';
import { formatDate } from '../utils/dateUtils';
import { getNotificationStatusLabel, normalizeNotificationStatusPayload } from '../utils/notificationStatus';
import './NotificationsTab.css';

const PAGE_SIZE = 10;

const NotificationsTab = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: PAGE_SIZE, total: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentCampaignStatus, setCurrentCampaignStatus] = useState(null);
  const [currentCampaignStatusError, setCurrentCampaignStatusError] = useState('');
  const statusPollRef = useRef(null);

  const clearStatusPoll = useCallback(() => {
    if (statusPollRef.current) {
      clearTimeout(statusPollRef.current);
      statusPollRef.current = null;
    }
  }, []);

  const loadCurrentCampaignStatus = useCallback(async (campaignId) => {
    if (!campaignId) {
      setCurrentCampaignStatus(null);
      return;
    }

    clearStatusPoll();
    setCurrentCampaignStatusError('');

    try {
      const result = await API.notifications.status(campaignId);

      if (!result.success) {
        setCurrentCampaignStatusError(result.error || 'Не удалось загрузить статус текущей рассылки.');
        return;
      }

      const campaign = result.data ? normalizeNotificationStatusPayload(result.data) : null;

      if (campaign) {
        setCurrentCampaignStatus(campaign);

        if (campaign.status === 'pending' || campaign.status === 'in_progress') {
          statusPollRef.current = setTimeout(() => loadCurrentCampaignStatus(campaignId), 5000);
        }
      }
    } catch (e) {
      setCurrentCampaignStatusError(e?.message || 'Не удалось загрузить статус текущей рассылки.');
    }
  }, [clearStatusPoll]);

  const loadLatestCampaignStatus = useCallback(async () => {
    try {
      const result = await API.notifications.list({ page: 1, limit: 1 });

      if (!result.success) {
        setCurrentCampaignStatusError(result.error || 'Не удалось загрузить последнюю рассылку.');
        return;
      }

      const latestCampaign = result.data?.data?.[0];

      if (!latestCampaign) {
        clearStatusPoll();
        setCurrentCampaignStatus(null);
        setCurrentCampaignStatusError('');
        return;
      }

      await loadCurrentCampaignStatus(latestCampaign.id);
    } catch (e) {
      setCurrentCampaignStatusError(e?.message || 'Не удалось загрузить последнюю рассылку.');
    }
  }, [clearStatusPoll, loadCurrentCampaignStatus]);

  const loadCampaigns = useCallback(async (page = 1) => {
    setLoading(true);
    setError('');

    try {
      const result = await API.notifications.list({ page, limit: PAGE_SIZE });

      if (!result.success) {
        setError(result.error || 'Не удалось загрузить историю рассылок.');
        return;
      }

      const response = result.data || {};
      setCampaigns(Array.isArray(response.data) ? response.data : []);
      setPagination(response.pagination || { page, limit: PAGE_SIZE, total: 0 });
    } catch (e) {
      setError(e?.message || 'Не удалось загрузить историю рассылок.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCampaigns(1);
    loadLatestCampaignStatus();

    return () => {
      clearStatusPoll();
    };
  }, [clearStatusPoll, loadCampaigns, loadLatestCampaignStatus]);

  const totalPages = Math.ceil((pagination.total || 0) / (pagination.limit || PAGE_SIZE));

  return (
    <div className="tab-content">
      <section className="notifications-current-status">
        <div className="notifications-current-status__header">
          <div>
            <h3>Статус текущей рассылки</h3>
            <p>Источник: `/admin/notifications/:id/status` для последней кампании.</p>
          </div>
          {currentCampaignStatus?.id && (
            <div className="notifications-current-status__campaign-id">
              Кампания #{currentCampaignStatus.id}
            </div>
          )}
        </div>

        {currentCampaignStatusError && (
          <div className="notifications-history__error">{currentCampaignStatusError}</div>
        )}

        {!currentCampaignStatus && !currentCampaignStatusError && (
          <div className="notifications-current-status__empty">
            Активных или завершённых рассылок пока нет.
          </div>
        )}

        {currentCampaignStatus && (
          <div className="notifications-current-status__grid">
            <div className="notifications-current-status__card">
              <span>Статус</span>
              <strong className={`notifications-history__status notifications-history__status--${currentCampaignStatus.status}`}>
                {getNotificationStatusLabel(currentCampaignStatus.status)}
              </strong>
            </div>
            <div className="notifications-current-status__card">
              <span>Получатели</span>
              <strong>{currentCampaignStatus.totalRecipients ?? 0}</strong>
            </div>
            <div className="notifications-current-status__card">
              <span>Отправлено</span>
              <strong>{currentCampaignStatus.sentCount ?? 0}</strong>
            </div>
            <div className="notifications-current-status__card">
              <span>Ошибки</span>
              <strong>{currentCampaignStatus.failedCount ?? 0}</strong>
            </div>
            <div className="notifications-current-status__card">
              <span>Обработано</span>
              <strong>{currentCampaignStatus.processedCount ?? 0}</strong>
            </div>
            <div className="notifications-current-status__card">
              <span>Осталось</span>
              <strong>{currentCampaignStatus.remainingCount ?? 0}</strong>
            </div>
            <div className="notifications-current-status__card">
              <span>Создана</span>
              <strong>{formatDate(currentCampaignStatus.createdAt) || '—'}</strong>
            </div>
            <div className="notifications-current-status__card">
              <span>Обновлена</span>
              <strong>{formatDate(currentCampaignStatus.updatedAt) || '—'}</strong>
            </div>
            <div className="notifications-current-status__card">
              <span>Старт</span>
              <strong>{formatDate(currentCampaignStatus.startedAt) || '—'}</strong>
            </div>
            <div className="notifications-current-status__card">
              <span>Завершение</span>
              <strong>{formatDate(currentCampaignStatus.finishedAt) || '—'}</strong>
            </div>
          </div>
        )}

        {currentCampaignStatus?.lastError && (
          <div className="notifications-current-status__last-error">
            Последняя ошибка: {currentCampaignStatus.lastError}
          </div>
        )}
      </section>

      <NotificationPanel
        onCampaignCreated={(campaign) => {
          loadCampaigns(1);
          if (campaign?.id) {
            loadCurrentCampaignStatus(campaign.id);
          } else {
            loadLatestCampaignStatus();
          }
        }}
      />

      <section className="notifications-history">
        <div className="notifications-history__header">
          <div>
            <h3>История рассылок</h3>
            <p>Источник: `/admin/notifications?page={pagination.page}&limit={pagination.limit}`.</p>
          </div>
        </div>

        {error && <div className="notifications-history__error">{error}</div>}

        <div className="notifications-history__table-wrap">
          <table className="notifications-history__table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Название</th>
                <th>Статус</th>
                <th>Получатели</th>
                <th>Отправлено</th>
                <th>Ошибки</th>
                <th>Создана</th>
                <th>Обновлена</th>
                <th>Старт</th>
                <th>Завершение</th>
              </tr>
            </thead>
            <tbody>
              {!loading && campaigns.length === 0 && (
                <tr>
                  <td colSpan="10" className="notifications-history__empty">
                    История рассылок пока пуста.
                  </td>
                </tr>
              )}

              {campaigns.map((campaign) => (
                <tr key={campaign.id}>
                  <td>{campaign.id}</td>
                  <td>{campaign.title || 'Без названия'}</td>
                  <td>
                    <span className={`notifications-history__status notifications-history__status--${campaign.status}`}>
                      {getNotificationStatusLabel(campaign.status)}
                    </span>
                  </td>
                  <td>{campaign.totalRecipients ?? 0}</td>
                  <td>{campaign.sentCount ?? 0}</td>
                  <td>{campaign.failedCount ?? 0}</td>
                  <td>{formatDate(campaign.createdAt) || '—'}</td>
                  <td>{formatDate(campaign.updatedAt) || '—'}</td>
                  <td>{formatDate(campaign.startedAt) || '—'}</td>
                  <td>{formatDate(campaign.finishedAt) || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {loading && <div className="notifications-history__loading">Загрузка истории...</div>}

        <div className="notifications-history__footer">
          <div className="notifications-history__meta">Всего кампаний: {pagination.total || 0}</div>
          <Pagination
            currentPage={pagination.page || 1}
            totalPages={totalPages}
            onPageChange={(page) => loadCampaigns(page)}
          />
        </div>
      </section>
    </div>
  );
};

export default NotificationsTab;
